import { baseApi } from '../../../core/baseApi';
import { conversationQueriesApi } from './queries';
import { unwrapApiResponse } from '../../../patterns/crud';
import type { GetConversationsParams } from '../types';

/**
 * Tracks conversation IDs updated by the current client within the last few seconds.
 * Used by WebSocket handlers (useGlobalConversationEvents) to suppress redundant
 * cache updates that would race with mutation optimistic patches.
 */
export const recentlyMutatedConversationIds = new Set<string>();

/**
 * Pending cleanup timers keyed by conversation ID.
 * Allows concurrent mutations on the same ID to cancel the previous timer so the
 * guard stays active until the last in-flight mutation settles.
 */
export const mutationTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

export const conversationMutationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Core mutations
    createConversation: builder.mutation({
      query: (args: unknown) => ({
        url: '/conversations',
        method: 'POST' as const,
        body: args && typeof args === 'object' && 'data' in args ? (args as { data: unknown }).data : args
      }),
      transformResponse: unwrapApiResponse,
      invalidatesTags: [
        { type: 'Conversations', id: 'LIST' },
      ]
    }),
    updateConversation: builder.mutation({
      query: (args: { id: string; data: unknown }) => ({
        url: `/conversations/${args.id}`,
        method: 'PUT' as const,
        body: args.data
      }),
      transformResponse: unwrapApiResponse,
      invalidatesTags: (result, error, args: { id: string }) => [
        { type: 'Conversations' as const, id: args.id },
        { type: 'Conversations' as const, id: 'LIST' },
      ]
    }),
    deleteConversation: builder.mutation({
      query: (args: { id: string } | string) => {
        const id = typeof args === 'string' ? args : args.id;
        return { url: `/conversations/${id}`, method: 'DELETE' as const };
      },
      transformResponse: unwrapApiResponse,
      async onQueryStarted(args: { id: string } | string, { dispatch, queryFulfilled, getState }) {
        const id = typeof args === 'string' ? args : args.id;
        const patchResults: Array<{ undo: () => void }> = [];

        const applyPatch = (endpointName: string, queryArgs: unknown) =>
          dispatch(
            conversationQueriesApi.util.updateQueryData(endpointName as 'getConversations', queryArgs as GetConversationsParams | undefined, (draft) => {
              if (!draft || !('items' in draft)) return;
              const idx = draft.items.findIndex(c => c.id === id);
              if (idx !== -1) {
                draft.items.splice(idx, 1);
                if (typeof draft.total === 'number') draft.total--;
              }
            })
          ) as unknown as { undo: () => void };

        try {
          const state = getState() as { api?: { queries?: Record<string, { endpointName?: string; originalArgs?: unknown }> } };
          const queries = state?.api?.queries || {};
          Object.values(queries).forEach((q) => {
            if (q && (q.endpointName === 'getConversations' || q.endpointName === 'getInfiniteConversations')) {
              patchResults.push(applyPatch(q.endpointName, q.originalArgs));
            }
          });
        } catch { /* noop */ }

        // Toast immediately with optimistic update — don't wait for server
        import('@/shared/utils/toast').then(({ showToast }) =>
          showToast.success('Conversation deleted'));

        try {
          await queryFulfilled;
        } catch {
          patchResults.forEach(p => p.undo());
          import('@/shared/utils/toast').then(({ showToast }) =>
            showToast.error('Failed to delete conversation'));
        }
      },
      invalidatesTags: (result, error, args: { id: string } | string) => {
        const id = typeof args === 'string' ? args : args.id;
        return [
          { type: 'Conversations' as const, id },
          { type: 'Conversations' as const, id: 'LIST' },
        ];
      }
    }),

    // Toggle mutations
    toggleConversationStatus: builder.mutation<unknown, { id: string; isActive: boolean; loggedBeforeClose?: boolean }>({
      query: ({ id, isActive, loggedBeforeClose }) => ({
        url: `/conversations/${id}/toggle-active`,
        method: 'POST',
        body: { isActive, ...(loggedBeforeClose ? { loggedBeforeClose } : {}) }
      }),
      async onQueryStarted({ id, isActive }, { dispatch, queryFulfilled, getState }) {
        // Guard: suppress WebSocket echo for this conversation.
        // Cancel any pending cleanup so concurrent mutations don't clear each other's guard.
        recentlyMutatedConversationIds.add(id);
        const existingTimeout = mutationTimeouts.get(id);
        if (existingTimeout) clearTimeout(existingTimeout);

        const patchResults: Array<{ undo: () => void }> = [];

        // Optimistic patch: detail cache
        patchResults.push(dispatch(
          conversationQueriesApi.util.updateQueryData('getConversationById', id, (draft) => {
            if (draft) {
              Object.assign(draft, { isActive, isOpen: isActive });
            }
          })
        ) as unknown as { undo: () => void });

        // Optimistic patch: all list caches
        const applyPatch = (endpointName: string, args: unknown) =>
          dispatch(
            conversationQueriesApi.util.updateQueryData(endpointName as 'getConversations', args as GetConversationsParams | undefined, (draft) => {
              if (!draft || !('items' in draft)) return;
              const idx = draft.items.findIndex(c => c.id === id);
              if (idx !== -1) {
                Object.assign(draft.items[idx], { isActive, isOpen: isActive });
              }
            })
          ) as unknown as { undo: () => void };

        try {
          const state = getState() as { api?: { queries?: Record<string, { endpointName?: string; originalArgs?: unknown }> } };
          const queries = state?.api?.queries || {};
          Object.values(queries).forEach((q) => {
            if (q && (q.endpointName === 'getConversations' || q.endpointName === 'getInfiniteConversations')) {
              patchResults.push(applyPatch(q.endpointName, q.originalArgs));
            }
          });
        } catch {/* noop */}

        // Toast immediately with optimistic update — don't wait for server
        import('@/shared/utils/toast').then(({ showToast }) =>
          showToast.success(isActive ? 'Conversation reopened' : 'Conversation closed'));

        try {
          await queryFulfilled;
          // Clear guard 2s after server confirms — covers the WS echo delivery window (~500ms)
          mutationTimeouts.set(id, setTimeout(() => {
            recentlyMutatedConversationIds.delete(id);
            mutationTimeouts.delete(id);
          }, 2000));
        } catch {
          // Mutation failed — no WS echo will arrive, clear guard immediately
          recentlyMutatedConversationIds.delete(id);
          mutationTimeouts.delete(id);
          patchResults.forEach(p => p.undo());
          import('@/shared/utils/toast').then(({ showToast }) =>
            showToast.error(isActive ? 'Failed to reopen' : 'Failed to close conversation'));
        }
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Conversations', id }
      ]
    }),

    toggleConversationMute: builder.mutation<unknown, { id: string; isMuted: boolean }>({
      query: ({ id, isMuted }) => ({
        url: `/conversations/${id}/toggle-mute`,
        method: 'POST',
        body: { isMuted }
      }),
      transformResponse: unwrapApiResponse,
      async onQueryStarted({ id, isMuted }, { dispatch, queryFulfilled, getState }) {
        // Guard: suppress WebSocket echo for this conversation.
        // Cancel any pending cleanup so concurrent mutations don't clear each other's guard.
        recentlyMutatedConversationIds.add(id);
        const existingTimeout = mutationTimeouts.get(id);
        if (existingTimeout) clearTimeout(existingTimeout);

        const patchResults: Array<{ undo: () => void }> = [];

        // Optimistic patch: detail cache
        patchResults.push(dispatch(
          conversationQueriesApi.util.updateQueryData('getConversationById', id, (draft) => {
            if (draft) {
              Object.assign(draft, { isMuted });
            }
          })
        ) as unknown as { undo: () => void });

        // Optimistic patch: all list caches
        const applyPatch = (endpointName: string, args: unknown) =>
          dispatch(
            conversationQueriesApi.util.updateQueryData(endpointName as 'getConversations', args as GetConversationsParams | undefined, (draft) => {
              if (!draft || !('items' in draft)) return;
              const idx = draft.items.findIndex(c => c.id === id);
              if (idx !== -1) {
                Object.assign(draft.items[idx], { isMuted });
              }
            })
          ) as unknown as { undo: () => void };

        try {
          const state = getState() as { api?: { queries?: Record<string, { endpointName?: string; originalArgs?: unknown }> } };
          const queries = state?.api?.queries || {};
          Object.values(queries).forEach((q) => {
            if (q && (q.endpointName === 'getConversations' || q.endpointName === 'getInfiniteConversations')) {
              patchResults.push(applyPatch(q.endpointName, q.originalArgs));
            }
          });
        } catch {/* noop */}

        // Toast immediately with optimistic update — don't wait for server
        import('@/shared/utils/toast').then(({ showToast }) =>
          showToast.success(isMuted ? 'Conversation muted' : 'Conversation unmuted'));

        try {
          await queryFulfilled;
          // Clear guard 2s after server confirms — covers the WS echo delivery window (~500ms)
          mutationTimeouts.set(id, setTimeout(() => {
            recentlyMutatedConversationIds.delete(id);
            mutationTimeouts.delete(id);
          }, 2000));
        } catch {
          // Mutation failed — no WS echo will arrive, clear guard immediately
          recentlyMutatedConversationIds.delete(id);
          mutationTimeouts.delete(id);
          patchResults.forEach(p => p.undo());
          import('@/shared/utils/toast').then(({ showToast }) =>
            showToast.error(isMuted ? 'Failed to mute' : 'Failed to unmute conversation'));
        }
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Conversations', id }
      ]
    }),

    // ATOMIC: Get or create conversation with contact and first message
    // Backend returns: { success, data: { conversation: {...}, message: {...}, isNewConversation } }
    // After unwrapApiResponse: { conversation: { id, ... }, message: {...}, isNewConversation }
    getOrCreateContactConversationWithMessage: builder.mutation<unknown, {
      contactId: string;
      content?: string;
      messageType?: string;
      deliveryChannel?: string;
      mediaUrls?: string[];
      metadata?: Record<string, unknown>
    }>({
      query: ({ contactId, ...messageData }) => ({
        url: `/conversations/contact/${contactId}/with-message`,
        method: 'POST',
        body: messageData,
      }),
      transformResponse: unwrapApiResponse,
      // NOTE: No invalidatesTags — the compose handler manually patches the
      // conversations list cache (lines 375-402 of useComposeHandler). Invalidating
      // here caused redundant Conversations list refetches that raced with seeded
      // data. The socket `conversation_updated` event handles ongoing sync.
    }),

    // ATOMIC: Get or create conversation with phone number and first message
    // Backend returns: { success, data: { conversation: {...}, message: {...}, isNewConversation } }
    // After unwrapApiResponse: { conversation: { id, ... }, message: {...}, isNewConversation }
    getOrCreatePhoneConversationWithMessage: builder.mutation<unknown, {
      phoneNumber: string;
      content?: string;
      messageType?: string;
      deliveryChannel?: string;
      mediaUrls?: string[];
      metadata?: Record<string, unknown>
    }>({
      query: ({ phoneNumber, ...messageData }) => ({
        url: `/conversations/phone/with-message`,
        method: 'POST',
        body: { phoneNumber, ...messageData }
      }),
      transformResponse: unwrapApiResponse,
      // NOTE: No invalidatesTags — same reasoning as contact variant above.
    }),

    // LEGACY: Get or create conversation with contact (no message)
    // Backend returns: { success, data: { id, ... } }
    // After unwrapApiResponse: { id, ... }
    getOrCreateContactConversation: builder.mutation<unknown, { contactId: string }>({
      query: ({ contactId }) => ({
        url: `/conversations/contact/${contactId}`,
        method: 'POST'
      }),
      transformResponse: unwrapApiResponse,
      invalidatesTags: (result) => {
        if (!result) return [];
        const conv = result as { id?: string };
        return [
          { type: 'Conversations', id: 'LIST' },
          ...(conv.id ? [{ type: 'Conversations' as const, id: conv.id }] : [])
        ];
      }
    }),

    // LEGACY: Get or create conversation with phone number (no message)
    // Backend returns: { success, data: { id, ... } }
    // After unwrapApiResponse: { id, ... }
    getOrCreatePhoneConversation: builder.mutation<unknown, { phoneNumber: string }>({
      query: ({ phoneNumber }) => ({
        url: `/conversations/phone`,
        method: 'POST',
        body: { phoneNumber }
      }),
      transformResponse: unwrapApiResponse,
      invalidatesTags: (result) => {
        if (!result) return [];
        const conv = result as { id?: string };
        return [
          { type: 'Conversations', id: 'LIST' },
          ...(conv.id ? [{ type: 'Conversations' as const, id: conv.id }] : [])
        ];
      }
    }),

    // Mark a conversation as read (clears unread count)
    markConversationAsRead: builder.mutation<unknown, { id: string }>({
      query: ({ id }) => ({
        url: `/conversations/${id}/mark-read`,
        method: 'POST',
        body: { isRead: true },
        fetchOptions: { keepalive: true },
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled, getState }) {
        // Guard: suppress WebSocket echo for this conversation.
        // Cancel any pending cleanup so concurrent mutations don't clear each other's guard.
        recentlyMutatedConversationIds.add(id);
        const existingTimeout = mutationTimeouts.get(id);
        if (existingTimeout) clearTimeout(existingTimeout);

        const patchResults: Array<{ undo: () => void }> = [];

        const applyPatch = (endpointName: string, args: unknown) =>
          dispatch(
            conversationQueriesApi.util.updateQueryData(endpointName as 'getConversations', args as GetConversationsParams | undefined, (draft) => {
              if (!draft || !('items' in draft)) return;
              const idx = draft.items.findIndex(c => c.id === id);
              if (idx !== -1) {
                Object.assign(draft.items[idx], { unreadCount: 0 });
              }
            })
          ) as unknown as { undo: () => void };

        patchResults.push(applyPatch('getConversations', undefined));

        try {
          const state = getState() as { api?: { queries?: Record<string, { endpointName?: string; originalArgs?: unknown }> } };
          const queries = state?.api?.queries || {};
          Object.values(queries).forEach((q) => {
            if (q && (q.endpointName === 'getConversations' || q.endpointName === 'getInfiniteConversations')) {
              patchResults.push(applyPatch(q.endpointName, q.originalArgs));
            }
          });
        } catch {/* noop */}

        try {
          await queryFulfilled;

          // Re-patch: ensure unreadCount = 0 in any caches that were populated
          // AFTER our initial optimistic patches (e.g., on page load where the
          // conversation list HTTP response arrives after the mutation started
          // and the initial patches were no-ops on the empty cache).
          try {
            const currentState = getState() as { api?: { queries?: Record<string, { endpointName?: string; originalArgs?: unknown }> } };
            const currentQueries = currentState?.api?.queries || {};
            Object.values(currentQueries).forEach((q) => {
              if (q && (q.endpointName === 'getConversations' || q.endpointName === 'getInfiniteConversations')) {
                dispatch(
                  conversationQueriesApi.util.updateQueryData(q.endpointName as 'getConversations', q.originalArgs as GetConversationsParams | undefined, (draft) => {
                    if (!draft || !('items' in draft)) return;
                    const idx = draft.items.findIndex(c => c.id === id);
                    if (idx !== -1 && draft.items[idx].unreadCount !== 0) {
                      draft.items[idx].unreadCount = 0;
                    }
                  })
                );
              }
            });
          } catch {/* best-effort */}

          // Clear guard 2s after server confirms — covers the WS echo delivery window (~500ms)
          mutationTimeouts.set(id, setTimeout(() => {
            recentlyMutatedConversationIds.delete(id);
            mutationTimeouts.delete(id);
          }, 2000));
        } catch {
          // Mutation failed — no WS echo will arrive, clear guard immediately
          recentlyMutatedConversationIds.delete(id);
          mutationTimeouts.delete(id);
          patchResults.forEach(p => p.undo());
        }
      },
      invalidatesTags: (_result, error, { id }) => {
        if (error) {
          return [
            { type: 'Conversations' as const, id },
            { type: 'Conversations' as const, id: 'LIST' },
          ];
        }
        return [];
      },
    }),

    // Send a Google review request for a conversation's contact
    sendReviewRequest: builder.mutation<
      { sent: boolean; message: string },
      { conversationId: string }
    >({
      query: ({ conversationId }) => ({
        url: `/review-request/${conversationId}/send`,
        method: 'POST',
        body: {},
      }),
      transformResponse: unwrapApiResponse<{ sent: boolean; message: string }>,
      invalidatesTags: (result) =>
        result?.sent
          ? [{ type: 'ReviewRequestStats', id: 'CURRENT_MONTH' }]
          : [],
    }),

    // NOTE: WebSocket subscriptions moved to websocket.ts to avoid duplication
  })
});

export const {
  useCreateConversationMutation,
  useUpdateConversationMutation,
  useDeleteConversationMutation,
  useToggleConversationStatusMutation,
  useToggleConversationMuteMutation,
  useGetOrCreateContactConversationWithMessageMutation,
  useGetOrCreatePhoneConversationWithMessageMutation,
  useGetOrCreateContactConversationMutation,
  useGetOrCreatePhoneConversationMutation,
  useMarkConversationAsReadMutation,
  useSendReviewRequestMutation,
} = conversationMutationsApi;
